import { useMutation } from '@tanstack/react-query';

import {
  postV1MigrationCandidateBusinessPartnerByIdAccept,
  postV1MigrationCandidateBusinessPartnerByIdReject,
} from '../../api/generated/pm';
import { invalidateCustomerSuggestions } from '../../hooks/queries/migration.queries';

/**
 * Mutation hooks for the dealer-scope Customer Suggestions feature.
 *
 * Colocated next to the page, wrapping the generated `@hey-api` SDK calls with
 * `throwOnError` so failures reject, and invalidating the list through the
 * shared `invalidateCustomerSuggestions()` helper that owns the cache key.
 */

/** Dismiss a Customer Suggestion the dealer does not recognise. */
export const useRejectCustomerSuggestion = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await postV1MigrationCandidateBusinessPartnerByIdReject({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateCustomerSuggestions,
  });

export interface AcceptCustomerSuggestionPayload {
  id: string;
  /**
   * MUST be one of the candidate's `matchDetails.top_targets[].external_id` —
   * the endpoint responds 400 for anything else. Callers guarantee this by
   * construction (the value comes from a rendered match row), not by validating.
   */
  externalId: string;
}

/**
 * Link ("Merge", user-facing) a Customer Suggestion to one of its Potential
 * Matches. Creates neither a Request Business Partner nor a Service Agreement —
 * the Service Agreement is created afterwards by the Connect wizard.
 */
export const useAcceptCustomerSuggestion = () =>
  useMutation({
    mutationFn: async ({ id, externalId }: AcceptCustomerSuggestionPayload) => {
      const { data } = await postV1MigrationCandidateBusinessPartnerByIdAccept({
        path: { id },
        body: { externalId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateCustomerSuggestions,
  });
